import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import {
  User,
  Building2,
  CreditCard,
  Save,
  Edit2,
  X,
  Loader2,
  CheckCircle,
  Receipt,
} from 'lucide-react'
import { toast } from 'react-toastify'
import BrandDetailModal from '../components/BrandDetailModal'
import { useNavigate, useLocation } from 'react-router-dom'

type Tab = 'profile' | 'brands' | 'billing'

interface Brand {
  id: string
  userBrandId?: string
  companyName: string
  domain?: string
  website?: string
  industry?: string
  description?: string
  aiIndexScore?: number
  // Brand-level subscription fields
  subscriptionProduct?: 'STARTER' | 'GROWTH' | 'PRO' | null
  subscriptionStatus?: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'CANCELED' | 'UNPAID'
  currentPeriodEnd?: string | null
  cancelAtPeriodEnd?: boolean
  trialEndsAt?: string | null
  analysisFrequency?: 'MONTHLY' | 'WEEKLY'
}

export default function Settings() {
  const { currentUser, refreshUser, switchBrand } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [, setSwitchingBrand] = useState<string | null>(null)

  // Determine active tab from URL path
  const getActiveTab = (): Tab => {
    const path = location.pathname
    if (path === '/settings/profile') return 'profile'
    if (path === '/settings/billing') return 'billing'
    // Default to brands tab for /settings
    return 'brands'
  }

  const activeTab = getActiveTab()

  // Function to change tabs by navigating to new URL
  const handleTabChange = (tab: Tab) => {
    if (tab === 'brands') {
      navigate('/settings')
    } else {
      navigate(`/settings/${tab}`)
    }
  }

  // Profile state
  const [profileEdit, setProfileEdit] = useState(false)
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  })
  const [originalProfileData, setOriginalProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  })

  // Brands state
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandCount, setBrandCount] = useState(0)

  // AI Usage state
  const [, setAiUsage] = useState<any | null>(null)
  const [, setLoadingAiUsage] = useState(false)

  // Brand detail modal state
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)

  // Load user data
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        email: currentUser.email || ''
      })
      setOriginalProfileData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        email: currentUser.email || ''
      })
    }
  }, [currentUser])

  // Load brands and AI usage
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true)
        const response = await api.get('/brands')
        const userBrands = response.data.brands || []
        setBrands(userBrands)
        setBrandCount(userBrands.length)
      } catch (error) {
        console.error('Failed to fetch brands:', error)
        toast.error('Failed to load brands')
      } finally {
        setLoading(false)
      }
    }

    const fetchAIUsage = async () => {
      if (!currentUser?.activeBrand?.id) return

      try {
        setLoadingAiUsage(true)
        const response = await api.get(`/brands/${currentUser.activeBrand.id}/ai-usage`)
        setAiUsage(response.data)
      } catch (error) {
        console.error('Failed to fetch AI usage:', error)
        // Don't show toast error for AI usage - it's not critical
      } finally {
        setLoadingAiUsage(false)
      }
    }

    if (activeTab === 'brands') {
      fetchBrands()
      fetchAIUsage()
    }
  }, [activeTab, currentUser?.activeBrand])

  // Auto-open brand modal if brandId is in query params (e.g., from expired subscription redirect)
  useEffect(() => {
    if (brands.length === 0) return

    const params = new URLSearchParams(location.search)
    const brandId = params.get('brandId')

    if (brandId && !selectedBrand) {
      const brand = brands.find(b => b.id === brandId)
      if (brand) {
        setSelectedBrand(brand)
        // Clear the query param after opening modal
        navigate('/settings/brands', { replace: true })
      }
    }
  }, [brands, location.search, selectedBrand, navigate])

  const handleProfileEdit = () => {
    setProfileEdit(true)
    setOriginalProfileData(profileData)
  }

  const handleProfileCancel = () => {
    setProfileEdit(false)
    setProfileData(originalProfileData)
  }

  const handleProfileSave = async () => {
    setSaving(true)
    try {
      await api.put('/user/profile', {
        firstName: profileData.firstName,
        lastName: profileData.lastName
      })
      toast.success('Profile updated successfully')
      setProfileEdit(false)
      setOriginalProfileData(profileData)
      await refreshUser()
    } catch (error) {
      toast.error('Failed to update profile')
      console.error('Error updating profile:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleActivateBrand = async (brandId: string, redirectToOnboarding: boolean = false) => {
    try {
      setSwitchingBrand(brandId)
      await switchBrand(brandId)

      if (redirectToOnboarding) {
        // Redirect to onboarding for new brand setup
        navigate('/onboarding')
      }
    } catch (error) {
      console.error('Failed to activate brand:', error)
      toast.error('Failed to switch brand')
    } finally {
      setSwitchingBrand(null)
    }
  }

  const isActiveBrand = (brandId: string) => {
    return currentUser?.activeBrand?.id === brandId
  }

  const tabs: { id: Tab; name: string; icon: typeof User }[] = [
    { id: 'brands', name: 'Brands', icon: Building2 },
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'billing', name: 'Billing', icon: Receipt }
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-base text-gray-600">
          Manage your profile, brands, and subscription
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
            {!profileEdit ? (
              <button
                onClick={handleProfileEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Edit2 className="h-4 w-4" />
                <span>Edit</span>
              </button>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleProfileCancel}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleProfileSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              {profileEdit ? (
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter first name"
                />
              ) : (
                <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg">{profileData.firstName || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              {profileEdit ? (
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter last name"
                />
              ) : (
                <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg">{profileData.lastName || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg">{profileData.email}</p>
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>
          </div>
        </div>
      )}

      {/* Brands Tab */}
      {activeTab === 'brands' && (
        <div className="space-y-6">

          {/* Your Brands Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Your Brands</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {brandCount} brand{brandCount !== 1 ? 's' : ''} total
                </p>
              </div>
              <button
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                onClick={() => navigate('/add-brand')}
              >
                <Building2 className="h-4 w-4" />
                <span>Add Brand</span>
              </button>
            </div>

            {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No brands found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription Tier
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Billing
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand) => {
                    const isActive = isActiveBrand(brand.id)
                    const getPlanLabel = () => {
                      if (brand.subscriptionStatus === 'TRIAL') return 'Trial (Starter)'
                      if (brand.subscriptionProduct === 'STARTER') return 'Starter'
                      if (brand.subscriptionProduct === 'GROWTH') return 'Growth'
                      if (brand.subscriptionProduct === 'PRO') return 'Pro'
                      return 'No Plan'
                    }

                    const getBillingDate = () => {
                      if (brand.subscriptionStatus === 'TRIAL' && brand.trialEndsAt) {
                        return new Date(brand.trialEndsAt).toLocaleDateString()
                      }
                      if (brand.currentPeriodEnd) {
                        return new Date(brand.currentPeriodEnd).toLocaleDateString()
                      }
                      return '-'
                    }

                    const getPlanBadgeColor = () => {
                      if (brand.subscriptionProduct === 'PRO') return 'bg-purple-100 text-purple-700'
                      if (brand.subscriptionProduct === 'GROWTH') return 'bg-blue-100 text-blue-700'
                      if (brand.subscriptionProduct === 'STARTER') return 'bg-green-100 text-green-700'
                      if (brand.subscriptionStatus === 'TRIAL') return 'bg-yellow-100 text-yellow-700'
                      return 'bg-gray-100 text-gray-700'
                    }

                    return (
                      <tr
                        key={brand.id}
                        onClick={() => setSelectedBrand(brand)}
                        className={`cursor-pointer transition-colors ${
                          isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <h3 className="text-sm font-medium text-gray-900">{brand.companyName}</h3>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {brand.domain ? (
                            <a
                              href={`https://${brand.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-indigo-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {brand.domain}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadgeColor()}`}>
                            {getPlanLabel()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getBillingDate()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedBrand(brand)
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Change Plan
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          </div>

          {/* Features List - Only show in settings mode */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">All Plans Include</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">AI visibility score & tracking</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">Search query analysis</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">Competitor monitoring</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">Category discovery</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">Content import & analysis</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-700">LLMS.txt generator</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              Growth & Pro unlock AI Marketing Insights, more providers, and higher limits
            </p>
          </div>

          {/* FAQ - Only show in settings mode */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Can I change the plan of my brand(s)?</h4>
                <p className="text-sm text-gray-600">
                  Yes, you can upgrade or downgrade at any time. Upgrades take effect immediately with prorated billing, and downgrades take effect at the end of your current billing period.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">What happens to my brands if I downgrade?</h4>
                <p className="text-sm text-gray-600">
                  Your brands are preserved and remain access to all content while on a paid plan. Only the frequency of analysis changes.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Can I get a refund?</h4>
                <p className="text-sm text-gray-600">
                  Because of the costs to run AI analysis and keep metrics up-to-date we do not offer refunds. You can cancel your brand subscription at anytime. Your brand will remain active through the end of its current billing period.
                </p>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Payment History</h2>
            <p className="text-sm text-gray-600 mt-1">
              View and download your past invoices from Stripe
            </p>
          </div>

          {/* Placeholder for Stripe Payment History */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12">
            <div className="text-center">
              <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Payment History Coming Soon
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                We're working on integrating your complete payment history from Stripe.
                You'll be able to view and download all your invoices here.
              </p>
              <div className="mt-6">
                <a
                  href="https://billing.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Visit Stripe Billing Portal
                </a>
              </div>
            </div>
          </div>

          {/* Placeholder Invoice List (for UI reference) */}
          <div className="mt-6 opacity-50">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Invoices</h3>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Receipt className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-400">Invoice #INV-XXXX</p>
                      <p className="text-xs text-gray-400">Date placeholder</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-semibold text-gray-400">$XX.XX</span>
                    <button className="text-xs text-gray-400 hover:text-gray-600" disabled>
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Brand Detail Modal */}
      {selectedBrand && (
        <BrandDetailModal
          brand={selectedBrand}
          isOpen={true}
          onClose={() => setSelectedBrand(null)}
          onSuccess={async () => {
            await refreshUser()
            const response = await api.get('/brands')
            const userBrands = response.data.brands || []
            setBrands(userBrands)
            setBrandCount(userBrands.length)
            setSelectedBrand(null)
          }}
          onActivateBrand={async (brandId) => {
            await handleActivateBrand(brandId, false)
            setSelectedBrand(null)
          }}
          isActiveBrand={isActiveBrand(selectedBrand.id)}
        />
      )}
    </div>
  )
}
